import React, { createContext, useState, useContext, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { syncCartToServer, getEventId, ValidatePromoCode, FetchAppSettings, GetCartId } from './api-funcs';
import { initFacebookPixel } from './facebookPixel';
import { loadGtag } from './loadGTag';
import LoadGTM from './loadGTM';

// Create Context
const PromoCodeContext = createContext();

// Custom hook to use PromoCodeInput
export const usePromoCodeInput = () => {
  return useContext(PromoCodeContext);
};

// Context Provider Component
export const PromoCodeProvider = ({ children }) => {
  const [promoCodeInput, setPromoCode] = useState(() => {
    return localStorage.getItem('promoCode') || ''; // Read from localStorage
  });

  const [promoCodeStatus, setPromoCodeStatus] = useState(''); 
  const [promoCodeFilter, setPromoCodeFilter] = useState(0);
  const [promoCodeClosed, setPromoCodeClosed] = useState(0);
  const [urlPromoCleared, setUrlPromoCleared] = useState(false);

  const updatePromoCode = async (eventId, input) => {
    let response;
    
    if (input !== '') {
      response = await ValidatePromoCode(eventId, input); // Wait for the result
    }

    if (response?.isValid === true) {
      setPromoCode(input);
      localStorage.setItem('promoCode', input);
    } else {
      setPromoCode('');
      localStorage.removeItem('promoCode');
    }

    setPromoCodeStatus(response?.reason || '');
    return response;
  };

  return (
    <PromoCodeContext.Provider 
      value={{ 
        promoCodeInput, 
        promoCodeStatus,
        setPromoCode,
        updatePromoCode,
        promoCodeFilter, 
        setPromoCodeFilter,
        promoCodeClosed,
        setPromoCodeClosed,
        urlPromoCleared, 
        setUrlPromoCleared
      }}
    >
      {children}
    </PromoCodeContext.Provider>
  );
};


// Create a Context for the Cart
const CartContext = createContext();

// Create a Cart Provider Component
export const CartProvider = ({ children }) => {
    const [globalCartId, SetGlobalCartId] = useState({
      cartID: '',
      cartContents: [],
    });
  
    const { promoCodeInput,updatePromoCode,setPromoCodeClosed } = usePromoCodeInput();
  
    const generateOrRetrieveCartID = async () => {

      const savedCartID = localStorage.getItem('cartID');
      
      //validate that the cart id exists and is valid
      //otherwise remove it from local storage
      if (savedCartID) {
        const cartConfirm = await GetCartId(savedCartID);
        
        if ((!cartConfirm?.CART_ID) || (cartConfirm?.CART_ID != savedCartID) ) {
          localStorage.removeItem('cartID');
          return
        }
        else
          return savedCartID;
      }
      else
        return
    };
  
    // Initialize the cartID on component mount
    useEffect(() => {
      const initializeCartID = async () => {
        const cartID = await generateOrRetrieveCartID();
        if (cartID) {
          SetGlobalCartId((prevState) => ({
            ...prevState,
            cartID: cartID,
          }));
        }
      };
    
      initializeCartID();
    }, []);
      
    // Update cart contents function
    const updateCartContents = async (eventId,newCartContents) => {

      const allowedKeys = ["cart_qty", 
                            "ticket_type", 
                            "ticket_id", 
                            "definition_id", 
                            "cart_item_id", 
                            "replacement",
                            "ticket_category"
                          ];

      // Map over newCartContents and extract only allowed keys
      const filteredNewCartContents = newCartContents.map(item =>
        Object.fromEntries(Object.entries(item).filter(([key]) => allowedKeys.includes(key)))
      );
      
      const mergedCart = [
        ...(globalCartId.cartContents || []), // Keep existing cart contents
        ...filteredNewCartContents // Merge new items directly
      ];

      // Sync the cart with the server
      let newCartId = null;
      let returnResult = true;

      try {
        newCartId = await syncCartToServer({
          eventId:eventId,
          cartID: globalCartId.cartID,
          promoCode: promoCodeInput,
          cartContents: mergedCart,
        });

        if (!newCartId.cartId) {
          localStorage.removeItem('cartID');
          returnResult = false; //some error occurred
        }
        else if (newCartId.cartId.length === 36) {
            localStorage.setItem('cartID', newCartId.cartId);

            // Update state with the synced cart ID and contents
            SetGlobalCartId({
                cartID: newCartId.cartId,
                cartContents: newCartId.cartContents,
            });

            if ((!promoCodeInput) && (newCartId.promoCode))
              updatePromoCode(eventId,newCartId.promoCode);
              setPromoCodeClosed(1); //prevent the home page pop-up from being displayed

        }
        else {
            localStorage.removeItem('cartID');
            SetGlobalCartId({
                cartID: '',
                cartContents: [],
            })
        }
      } catch (error) {
        console.error('Error syncing cart to server:', error);
      
      }
      
      return returnResult;
    };
  
    return (
      <CartContext.Provider value={{ globalCartId, SetGlobalCartId, updateCartContents }}>
        {children}
      </CartContext.Provider>
    );
  };
  

// Custom hook to access CartContext
export const useCart = () => {
  return useContext(CartContext);
};

export const EventContext = createContext({
  globalEventId: null,
  loading: true,
  error: null,
  settings: null,
  accountToken: null
});

// Provider Component for EventContext
export const EventProvider = ({ eventUrl, children }) => {
  
  const [globalEventId, setGlobalEventId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [settings, setSettings] = useState(null);
  const [accountToken, setAccountToken] = useState(null);

  useEffect(() => {
      if (!eventUrl) return; // Don't run if eventUrl is missing

    const fetchEventId = async () => {
      setLoading(true); // Start loading
      try {
        if (eventUrl) {
          const eventId = await getEventId(eventUrl); // Get the event ID dynamically
          setGlobalEventId(eventId); // Set the event ID
        } else {
          console.error('Event URL is required');
        }
      } catch (err) {
        setError(err.message); // Handle error
      } finally {
        setLoading(false); // End loading
      }
    };

    fetchEventId(); // Trigger the fetch
  }, [eventUrl]); // Re-fetch if eventUrl changes

  // Fetch settings when the app starts
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        if (globalEventId) {
          const response = await FetchAppSettings(globalEventId);
          
          setSettings(response);

          if (response.FB_PIXEL) {
            initFacebookPixel(response.FB_PIXEL);
          }

          /*if (response.GTAG_ID) {
            //loadGtag(response.GTAG_ID);
            LoadGTM(response.GTAG_ID);
          }*/
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };

    fetchSettings();
  }, [globalEventId]);


  // Provide the event ID, loading, and error states to the context
  return (
    <>
    {settings?.GTAG_ID && <LoadGTM gtmId={settings.GTAG_ID} />}
    <EventContext.Provider value={{ globalEventId, loading, error, settings, accountToken, setAccountToken }}>
      {children}
    </EventContext.Provider>
    </>
  );
};



// Custom hook to access EventContext
export const useEvent = () => {
  const context = useContext(EventContext);
  if (!context) {
    console.error('useEvent must be used within an EventProvider');
  }
  return context;
};

//code for the page header timer context
// Create TimerContext
const TimerContext = createContext();

// Provide TimerContext to the app
export const TimerProvider = ({ children }) => {
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes
  const [isTimeActive, setIsTimeActive] = useState(false);

  const startTimer = () => setIsTimeActive(true);
  const stopTimer = () => setIsTimeActive(false);
  const resetTimer = () => {
    setTimeLeft(600); // Reset to 10 minutes
    setIsTimeActive(false); // Stop the timer
  };

  return (
    <TimerContext.Provider value={{ timeLeft, setTimeLeft, isTimeActive, startTimer, stopTimer, resetTimer }}>
      {children}
    </TimerContext.Provider>
  );
};

// Custom hook to use TimerContext
export const useTimer = () => {
  const context = useContext(TimerContext);

  // Ensure the context is used within a provider
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }

  return context;
};